import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, RefreshCw, Users, Download, Trash2, MailPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGACY_EVENT = "Masterclass Gospel — 26 septembre";
const PIANO_EVENT = "Piano Gospel — 10 octobre";
const eventOf = (r) => r.evenement || LEGACY_EVENT;

const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "legacy", label: "Septembre" },
  { id: "piano", label: "Piano — 10 octobre" },
];
const matchFilter = (r, f) =>
  f === "all" ? true : f === "legacy" ? eventOf(r) === LEGACY_EVENT : eventOf(r) === PIANO_EVENT;

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("fr-BE", { dateStyle: "medium", timeStyle: "short" });

export default function AdminPage() {
  const [key, setKey] = useState(() => localStorage.getItem("cmc_admin_key") || "");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [relanceLoading, setRelanceLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/inscriptions`, { headers: { "x-admin-key": k } });
      setRows(data);
      localStorage.setItem("cmc_admin_key", k);
    } catch (err) {
      setRows(null);
      setError(err.response?.status === 401 ? "Clé invalide. Réessayez." : "Impossible de charger les inscriptions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    const filtered = (rows || []).filter((r) => matchFilter(r, filter));
    const header = ["Reçue le", "Prénom", "Nom", "Email", "Téléphone", "Niveau musical", "Source", "Événement"];
    const lines = filtered.map((r) => [
      fmtDate(r.created_at), r.prenom, r.nom, r.email, r.telephone, r.niveau_musical, r.source, eventOf(r),
    ]);
    const csv = [header, ...lines]
      .map((l) => l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inscriptions-masterclass-gospel.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Supprimer définitivement cette inscription ?")) return;
    try {
      await axios.delete(`${API}/inscriptions/${id}`, { headers: { "x-admin-key": key } });
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch {
      alert("Suppression impossible. Réessayez.");
    }
  };

  const relancer = async () => {
    const cibles = new Set(
      (rows || []).filter((r) => eventOf(r) === LEGACY_EVENT).map((r) => r.email.toLowerCase())
    ).size;
    if (cibles === 0) {
      toast.info("Aucun inscrit de septembre à relancer.");
      return;
    }
    if (!window.confirm(`Envoyer l'invitation « Piano Gospel — 10 octobre » aux ${cibles} inscrits de septembre ?`)) return;
    setRelanceLoading(true);
    try {
      const { data } = await axios.post(`${API}/relance`, {}, { headers: { "x-admin-key": key } });
      toast.success(`${data.envoyes} invitation(s) envoyée(s)${data.echecs ? `, ${data.echecs} échec(s)` : ""}.`);
    } catch {
      toast.error("Erreur lors de l'envoi des invitations.");
    } finally {
      setRelanceLoading(false);
    }
  };

  if (rows === null) {
    return (
      <div className="min-h-screen bg-[#2C1E16] flex items-center justify-center px-6 grain relative" data-testid="admin-login">
        <form
          onSubmit={(e) => { e.preventDefault(); load(key); }}
          className="w-full max-w-sm bg-[#FDFBF7] rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.3)]"
        >
          <span className="w-11 h-11 rounded-full bg-[#D95D39] text-[#FDFBF7] flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </span>
          <h1 className="mt-5 font-display font-black text-2xl text-[#2C1E16] tracking-tight">
            Espace organisateur
          </h1>
          <p className="mt-2 text-sm text-[#5C4A3D]">
            Entrez la clé administrateur pour consulter les inscriptions reçues.
          </p>
          <Input
            type="password"
            data-testid="admin-key-input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Clé administrateur"
            className="mt-6 h-12 border-[#E8DCD0] focus-visible:ring-[#D95D39]"
            required
          />
          {error && (
            <p data-testid="admin-error" className="mt-3 text-sm font-semibold text-[#7A2021]">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-6 w-full rounded-full bg-[#2C1E16] text-[#FDFBF7] py-3 text-sm font-bold transition-[transform,background-color] duration-300 hover:scale-[1.02] hover:bg-[#D95D39] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Accéder aux inscriptions
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1512]" data-testid="admin-dashboard">
      <header className="border-b border-[#E8DCD0] bg-[#FDFBF7]/80 backdrop-blur-xl backdrop-saturate-150 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <p className="uppercase tracking-[0.25em] text-[10px] font-bold text-[#D95D39]">CMC-Belgique</p>
            <h1 className="font-display font-black text-2xl tracking-tight text-[#2C1E16]">
              Inscriptions — Masterclass Piano Gospel
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 rounded-full bg-[#F0A202]/20 text-[#2C1E16] px-4 py-2 text-sm font-bold" data-testid="admin-count">
              <Users className="w-4 h-4 text-[#D95D39]" />
              {rows.length} inscription{rows.length > 1 ? "s" : ""}
            </span>
            <button
              data-testid="admin-relance-button"
              onClick={relancer}
              disabled={relanceLoading}
              className="flex items-center gap-2 rounded-full bg-[#7A2021] text-[#FDFBF7] px-4 py-2 text-sm font-bold transition-[transform,background-color] duration-300 hover:scale-105 hover:bg-[#D95D39] disabled:opacity-60 disabled:hover:scale-100"
            >
              {relanceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailPlus className="w-4 h-4" />}
              Relancer septembre
            </button>
            <button
              data-testid="admin-export-csv-button"
              onClick={exportCsv}
              disabled={rows.length === 0}
              className="flex items-center gap-2 rounded-full bg-[#2C1E16] text-[#FDFBF7] px-4 py-2 text-sm font-bold transition-[transform,background-color] duration-300 hover:scale-105 hover:bg-[#D95D39] disabled:opacity-50 disabled:hover:scale-100"
            >
              <Download className="w-4 h-4" />
              Exporter CSV
            </button>
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(key)}
              className="rounded-full border border-[#E8DCD0] p-2.5 transition-[transform,background-color] duration-300 hover:scale-105 hover:bg-[#F0A202]/20"
              aria-label="Rafraîchir"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-3 mb-6" data-testid="admin-event-filters">
          {FILTERS.map((f) => {
            const n = rows.filter((r) => matchFilter(r, f.id)).length;
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-5 py-2 text-sm font-bold border transition-[transform,background-color,color] duration-300 hover:scale-105 ${
                  active
                    ? "bg-[#2C1E16] text-[#FDFBF7] border-[#2C1E16]"
                    : "bg-white text-[#2C1E16] border-[#E8DCD0] hover:bg-[#F0A202]/20"
                }`}
              >
                {f.label} · {n}
              </button>
            );
          })}
        </div>
        {rows.length === 0 ? (
          <p data-testid="admin-empty" className="text-center text-[#5C4A3D] py-20">
            Aucune inscription pour le moment. Partagez le formulaire !
          </p>
        ) : rows.filter((r) => matchFilter(r, filter)).length === 0 ? (
          <p data-testid="admin-empty-filter" className="text-center text-[#5C4A3D] py-20">
            Aucune inscription pour cet événement pour le moment.
          </p>
        ) : (
          <div className="border border-[#E8DCD0] rounded-lg overflow-x-auto bg-white">
            <Table data-testid="admin-table">
              <TableHeader>
                <TableRow className="bg-[#2C1E16] hover:bg-[#2C1E16]">
                  {["Reçue le", "Prénom", "Nom", "Email", "Téléphone", "Niveau", "Source", "Événement", ""].map((h, i) => (
                    <TableHead key={i} className="text-[#FDFBF7] font-bold uppercase tracking-wider text-xs whitespace-nowrap">
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.filter((r) => matchFilter(r, filter)).map((r) => (
                  <TableRow key={r.id} data-testid={`admin-row-${r.id}`} className="border-[#E8DCD0]">
                    <TableCell className="whitespace-nowrap text-[#5C4A3D]">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="font-semibold">{r.prenom}</TableCell>
                    <TableCell className="font-semibold">{r.nom}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell className="whitespace-nowrap">{r.telephone}</TableCell>
                    <TableCell>
                      <span className="rounded-full bg-[#F0A202]/20 px-3 py-1 text-xs font-bold text-[#2C1E16]">
                        {r.niveau_musical}
                      </span>
                    </TableCell>
                    <TableCell className="text-[#5C4A3D]">{r.source}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                        eventOf(r) === PIANO_EVENT ? "bg-[#D95D39]/15 text-[#D95D39]" : "bg-[#2C1E16]/10 text-[#5C4A3D]"
                      }`}>
                        {eventOf(r) === PIANO_EVENT ? "10 oct." : "26 sept."}
                      </span>
                    </TableCell>
                    <TableCell>
                      <button
                        data-testid={`admin-delete-${r.id}`}
                        onClick={() => deleteRow(r.id)}
                        className="rounded-full p-2 text-[#7A2021] transition-[transform,background-color] duration-300 hover:scale-110 hover:bg-[#7A2021]/10"
                        aria-label={`Supprimer l'inscription de ${r.prenom} ${r.nom}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>
    </div>
  );
}
